const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

async function request(path, options = {}) {
  const headers = {
    Accept: 'application/json',
    ...(options.headers || {}),
  };

  let body = options.body;
  if (
    body !== undefined &&
    body !== null &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer) &&
    !Object.keys(headers).some((name) => name.toLowerCase() === 'content-type')
  ) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    body,
    credentials: 'include',
  });

  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const error = new Error(
      data?.error || data?.message || `Erreur serveur (${response.status})`
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export const adminApi = {
  login(email, password) {
    return request('/admin/session/login', {
      method: 'POST',
      body: { email, password },
    });
  },

  logout() {
    return request('/admin/session/logout', { method: 'POST' });
  },

  me() {
    return request('/admin/session/me');
  },

  premiumAccessAudit(filters = {}) {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(name, String(value));
      }
    }
    return request(`/admin/billing/premium-audit?${params.toString()}`);
  },

  premiumAccessAuditEvents(userId) {
    return request(`/admin/billing/premium-audit/${encodeURIComponent(userId)}/events`);
  },

  blockCandidatePremium(userId, reason) {
    return request(`/admin/billing/premium-audit/${encodeURIComponent(userId)}/block`, {
      method: 'POST', body: { reason },
    });
  },

  unblockCandidatePremium(userId, reason) {
    return request(`/admin/billing/premium-audit/${encodeURIComponent(userId)}/unblock`, {
      method: 'POST', body: { reason },
    });
  },

  paymentHistory(filters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request(`/admin/billing/payments/history${query ? `?${query}` : ''}`);
  },

  mobileMoneyPending() {
    return request('/admin/billing/mobile-money/pending');
  },

  simulateMobileMoneyPayment(paymentId, outcome) {
    return request(`/admin/billing/mobile-money/${encodeURIComponent(paymentId)}/simulate`, {
      method: 'POST', body: { outcome },
    });
  },

  billingPlans() {
    return request('/admin/billing/plans');
  },

  createBillingPlan(payload) {
    return request('/admin/billing/plans', {
      method: 'POST',
      body: payload,
    });
  },

  updateBillingPlan(code, payload) {
    return request(`/admin/billing/plans/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  referralSettings() {
    return request('/admin/referrals');
  },

  referralCatalog() {
    return request('/admin/referrals/catalog');
  },

  updateReferralSettings(payload) {
    return request('/admin/referrals', { method: 'PATCH', body: payload });
  },

  users(filters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request(`/admin/users${query ? `?${query}` : ''}`);
  },

  userDetail(id) {
    return request(`/admin/users/${encodeURIComponent(id)}`);
  },

  updateUserProfile(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/profile`, {
      method: 'PATCH',
      body: payload,
    });
  },

  suspendUser(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/suspend`, {
      method: 'POST',
      body: payload,
    });
  },

  reactivateUser(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/reactivate`, {
      method: 'POST',
      body: {},
    });
  },

  disableUser(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/disable`, {
      method: 'POST',
      body: payload,
    });
  },

  forceLogoutUser(id, reason) {
    return request(`/admin/users/${encodeURIComponent(id)}/force-logout`, {
      method: 'POST',
      body: { reason },
    });
  },

  unlockUserLogin(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/unlock-login`, {
      method: 'POST',
      body: {},
    });
  },

  sendUserPasswordReset(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/password-reset`, {
      method: 'POST',
      body: {},
    });
  },

  sendUserEmailVerification(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/email-verification`, {
      method: 'POST',
      body: {},
    });
  },

  suspendUserForum(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/forum/suspend`, {
      method: 'POST',
      body: payload,
    });
  },

  reactivateUserForum(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/forum/reactivate`, {
      method: 'POST',
      body: {},
    });
  },

  deleteUserForumTopic(userId, topicId) {
    return request(
      `/admin/users/${encodeURIComponent(userId)}/forum/topics/${encodeURIComponent(topicId)}`,
      { method: 'DELETE' }
    );
  },

  deleteUserForumResponse(userId, responseId) {
    return request(
      `/admin/users/${encodeURIComponent(userId)}/forum/responses/${encodeURIComponent(responseId)}`,
      { method: 'DELETE' }
    );
  },

  grantUserPremium(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/premium`, {
      method: 'POST',
      body: payload,
    });
  },

  extendUserPremium(id, payload) {
    return request(`/admin/users/${encodeURIComponent(id)}/premium`, {
      method: 'PATCH',
      body: payload,
    });
  },

  revokeUserPremium(id, reason) {
    return request(`/admin/users/${encodeURIComponent(id)}/premium`, {
      method: 'DELETE',
      body: { reason },
    });
  },

  exportUser(id) {
    return request(`/admin/users/${encodeURIComponent(id)}/export`);
  },

  requestUserDeletion(id, reason) {
    return request(`/admin/users/${encodeURIComponent(id)}/deletion-request`, {
      method: 'POST',
      body: { reason },
    });
  },

  anonymizeUser(id, reason) {
    return request(`/admin/users/${encodeURIComponent(id)}/anonymize`, {
      method: 'POST',
      body: { reason, confirm: 'ANONYMIZE' },
    });
  },

  operators() {
    return request('/admin/operators');
  },

  inviteOperator(payload) {
    return request('/admin/operators/invitations', {
      method: 'POST',
      body: payload,
    });
  },

  acceptOperatorInvitation(token, password) {
    return request('/admin/operators/invitations/accept', {
      method: 'POST',
      body: { token, password },
    });
  },

  revokeOperatorInvitation(id) {
    return request(`/admin/operators/invitations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  updateOperator(id, payload) {
    return request(`/admin/operators/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  revokeOperatorSessions(id) {
    return request(`/admin/operators/${encodeURIComponent(id)}/revoke-sessions`, {
      method: 'POST',
      body: {},
    });
  },

  catalog(options = {}) {
    const params = new URLSearchParams();
    if (options.includeCourses === false) {
      params.set('includeCourses', 'false');
    }
    if (options.includeResources === false) {
      params.set('includeResources', 'false');
    }
    const query = params.toString();
    return request(`/admin/content${query ? `?${query}` : ''}`);
  },

  quizSubjects(filters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request('/admin/quiz-subjects' + (query ? '?' + query : ''));
  },

  createQuizSubject(payload) {
    return request('/admin/quiz-subjects', {
      method: 'POST',
      body: payload,
    });
  },

  updateQuizSubject(id, payload) {
    return request(`/admin/quiz-subjects/${encodeURIComponent(id)}/metadata`, {
      method: 'PATCH',
      body: payload,
    });
  },

  quizQuestions(subjectId) {
    return request(
      `/admin/quiz-subjects/${encodeURIComponent(subjectId)}/questions`
    );
  },

  createQuizQuestion(subjectId, payload) {
    return request(
      `/admin/quiz-subjects/${encodeURIComponent(subjectId)}/questions`,
      { method: 'POST', body: payload }
    );
  },

  updateQuestion(questionId, payload) {
    return request(`/admin/questions/${encodeURIComponent(questionId)}`, {
      method: 'PUT',
      body: payload,
    });
  },

  archiveQuestion(questionId) {
    return request(`/admin/questions/${encodeURIComponent(questionId)}`, {
      method: 'DELETE',
    });
  },

  modules() {
    return request('/admin/modules');
  },

  createModule(payload) {
    return request('/admin/modules', { method: 'POST', body: payload });
  },

  updateModule(id, payload) {
    return request(`/admin/modules/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: payload,
    });
  },

  courses(filters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request(`/admin/courses${query ? `?${query}` : ''}`);
  },

  importCourses(payload) {
    return request('/admin/courses/import', { method: 'POST', body: payload });
  },

  reorderCourseChapters(courseId, ids) {
    return request(`/admin/courses/${encodeURIComponent(courseId)}/reorder-chapters`, {
      method: 'PUT', body: { ids },
    });
  },

  reorderChapterSections(chapterId, ids) {
    return request(`/admin/chapters/${encodeURIComponent(chapterId)}/reorder-sections`, {
      method: 'PUT', body: { ids },
    });
  },

  createCourse(payload) {
    return request('/admin/courses', { method: 'POST', body: payload });
  },

  courseStructure(courseId) {
    return request(
      `/admin/courses/${encodeURIComponent(courseId)}/structure`
    );
  },

  updateCourse(courseId, payload) {
    return request(`/admin/courses/${encodeURIComponent(courseId)}`, {
      method: 'PUT',
      body: payload,
    });
  },

  updateCourseMetadata(courseId, payload) {
    return request(
      `/admin/courses/${encodeURIComponent(courseId)}/metadata`,
      { method: 'PATCH', body: payload }
    );
  },

  createChapter(courseId, payload) {
    return request(
      `/admin/courses/${encodeURIComponent(courseId)}/chapters`,
      { method: 'POST', body: payload }
    );
  },

  updateChapter(chapterId, payload) {
    return request(`/admin/chapters/${encodeURIComponent(chapterId)}`, {
      method: 'PUT',
      body: payload,
    });
  },

  deleteChapter(chapterId) {
    return request(`/admin/chapters/${encodeURIComponent(chapterId)}`, {
      method: 'DELETE',
    });
  },

  createSection(chapterId, payload) {
    return request(
      `/admin/chapters/${encodeURIComponent(chapterId)}/sections`,
      { method: 'POST', body: payload }
    );
  },

  updateSection(sectionId, payload) {
    return request(`/admin/sections/${encodeURIComponent(sectionId)}`, {
      method: 'PUT',
      body: payload,
    });
  },

  deleteSection(sectionId) {
    return request(`/admin/sections/${encodeURIComponent(sectionId)}`, {
      method: 'DELETE',
    });
  },

  simulationCatalog(kind, filters = {}) {
    if (!['lots', 'subjects'].includes(kind)) throw new Error('Catalogue invalide');
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request(`/admin/simulation-catalog/${kind}${query ? `?${query}` : ''}`);
  },

  simulationLotOptions() {
    return request('/admin/simulation-catalog/lot-options');
  },

  simulationLotDetail(id) {
    return request(`/admin/simulation-catalog/lots/${encodeURIComponent(id)}`);
  },

  simulationSubjectDetail(id) {
    return request(`/admin/simulation-catalog/subjects/${encodeURIComponent(id)}`);
  },

  createSimulationLot(payload) {
    return request('/admin/simulation-lots', {
      method: 'POST',
      body: payload,
    });
  },

  updateSimulationLot(lotId, payload) {
    return request(
      `/admin/simulation-lots/${encodeURIComponent(lotId)}`,
      { method: 'PATCH', body: payload }
    );
  },

  createSimulation(payload) {
    return request('/admin/simulations', { method: 'POST', body: payload });
  },

  updateSimulation(simulationId, payload) {
    return request(
      `/admin/simulations/${encodeURIComponent(simulationId)}`,
      { method: 'PATCH', body: payload }
    );
  },

  simulationQuestions(simulationId) {
    return request(
      `/admin/simulations/${encodeURIComponent(simulationId)}/questions`
    );
  },

  createSimulationQuestion(simulationId, payload) {
    return request(
      `/admin/simulations/${encodeURIComponent(simulationId)}/questions`,
      { method: 'POST', body: payload }
    );
  },

  reorderSimulationQuestions(simulationId, ids) {
    return request(`/admin/simulations/${encodeURIComponent(simulationId)}/reorder-questions`, {
      method: 'PUT',
      body: { ids },
    });
  },

  updateSimulationQuestionOrder(simulationId, questionId, ordre) {
    return request(
      `/admin/simulations/${encodeURIComponent(
        simulationId
      )}/questions/${encodeURIComponent(questionId)}/order`,
      { method: 'PATCH', body: { ordre } }
    );
  },

  detachSimulationQuestion(simulationId, questionId) {
    return request(
      `/admin/simulations/${encodeURIComponent(
        simulationId
      )}/questions/${encodeURIComponent(questionId)}`,
      { method: 'DELETE' }
    );
  },

  resources(filters = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    return request(`/admin/resources${query ? `?${query}` : ''}`);
  },

  resource(resourceId) {
    return request(`/admin/resources/${encodeURIComponent(resourceId)}`);
  },

  previewResource(resourceId) {
    return request(
      `/admin/resources/${encodeURIComponent(resourceId)}/preview`
    );
  },

  createResource(payload) {
    return request('/admin/resources', { method: 'POST', body: payload });
  },

  updateResource(resourceId, payload) {
    return request(`/admin/resources/${encodeURIComponent(resourceId)}`, {
      method: 'PUT',
      body: payload,
    });
  },

  archiveResource(resourceId) {
    return request(`/admin/resources/${encodeURIComponent(resourceId)}`, {
      method: 'DELETE',
    });
  },

  async uploadResourceFile(file) {
    const response = await fetch(`${API_URL}/admin/resource-files`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': encodeURIComponent(file.name),
      },
      body: file,
    });

    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error(
        data?.error || `Erreur serveur (${response.status})`
      );
      error.status = response.status;
      error.data = data;
      throw error;
    }
    return data;
  },

  async downloadXlsxTemplate(kind) {
    const response = await fetch(`${API_URL}/admin/bulk-import/template/${encodeURIComponent(kind)}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      const error = new Error(payload?.error || 'Téléchargement du modèle Excel impossible');
      error.status = response.status;
      throw error;
    }
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
      throw new Error('Le serveur n’a pas retourné un fichier Excel valide.');
    }
    return response.blob();
  },

  async inspectWorkbookSheets(file) {
    return request('/admin/bulk-import/sheets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      body: file,
    });
  },

  async bulkImportCatalog(kind, file, dryRun, previewSha = '', sheet = '') {
    const excel = file.name?.toLowerCase().endsWith('.xlsx');
    const params = new URLSearchParams({ dry_run: String(dryRun) });
    if (excel) params.set('sheet', sheet);
    const payload = excel ? file : JSON.parse(await file.text());
    return request('/admin/bulk-import/catalog/' + encodeURIComponent(kind) + '?' + params, {
      method: 'POST',
      headers: {
        'Content-Type': excel
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/json',
        ...(previewSha ? { 'X-Import-Preview-Sha256': previewSha } : {}),
      },
      body: payload,
    });
  },

  bulkImportCoursesXlsx(file, sheet, dryRun, moduleId, previewSha = '') {
    const params = new URLSearchParams({ sheet, dry_run: String(dryRun) });
    if (moduleId) params.set('module_id', String(moduleId));
    return request('/admin/bulk-import/courses?' + params, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ...(previewSha ? { 'X-Import-Preview-Sha256': previewSha } : {}),
      },
      body: file,
    });
  },

  async bulkImportQuestions(kind, subjectId, file, dryRun, previewSha = '', sheet = '') {
    if (!['quiz', 'simulation'].includes(kind)) throw new Error('Type de sujet invalide');
    const excel = file.name?.toLowerCase().endsWith('.xlsx');
    const params = new URLSearchParams({ dry_run: String(dryRun) });
    if (excel && sheet) params.set('sheet', sheet);
    return request(`/admin/bulk-import/${kind}/${encodeURIComponent(subjectId)}?${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': excel
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/json',
        ...(previewSha ? { 'X-Import-Preview-Sha256': previewSha } : {}),
      },
      body: excel ? file : JSON.parse(await file.text()),
    });
  },

  attachResourceFile(resourceId, metadata) {
    return request(
      `/admin/resources/${encodeURIComponent(resourceId)}/file`,
      { method: 'PATCH', body: metadata }
    );
  },
};

export { API_URL };
